import type {
  TherapistWithProfile,
  TherapistFormData,
  Profile,
  WorkingHour,
  WorkingHourFormData,
  BlockedPeriod,
  BlockedPeriodFormData,
  SearchFilters,
  PaginatedResponse,
} from '@leoclinic/types';
import { getSupabaseClient } from '../supabase/client';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Serviço de terapeutas — Repository Pattern
 */
export class TherapistService {
  async list(
    clinicId: string,
    filters: SearchFilters = {},
  ): Promise<PaginatedResponse<TherapistWithProfile>> {
    const supabase = getSupabaseClient();
    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE;

    const { data: therapists, error } = await supabase
      .from('therapists')
      .select('*')
      .eq('clinic_id', clinicId)
      .neq('status', 'archived')
      .order('created_at', { ascending: false });

    if (error) throw error;

    const enriched = await this.enrichWithProfiles(therapists ?? []);

    let filtered = enriched;
    if (filters.query?.trim()) {
      const q = filters.query.trim().toLowerCase();
      filtered = enriched.filter(
        (t) =>
          t.full_name.toLowerCase().includes(q) ||
          t.email.toLowerCase().includes(q) ||
          (t.specialty?.toLowerCase().includes(q) ?? false),
      );
    }

    if (filters.status) {
      filtered = filtered.filter((t) => t.status === filters.status);
    }

    const total = filtered.length;
    const from = (page - 1) * pageSize;
    const data = filtered.slice(from, from + pageSize);

    return {
      data,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize) || 1,
    };
  }

  async getById(id: string): Promise<TherapistWithProfile | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.from('therapists').select('*').eq('id', id).single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    const enriched = await this.enrichWithProfiles([data]);
    return enriched[0] ?? null;
  }

  async getAvailableProfiles(clinicId: string): Promise<Profile[]> {
    const supabase = getSupabaseClient();

    const [{ data: therapistRows }, { data: employees, error: empError }] = await Promise.all([
      supabase.from('therapists').select('employee_id').eq('clinic_id', clinicId).neq('status', 'archived'),
      supabase.from('employees').select('id, profile_id').eq('clinic_id', clinicId),
    ]);

    if (empError) throw empError;

    const employeeToProfile = new Map((employees ?? []).map((e) => [e.id, e.profile_id]));
    const usedProfileIds = new Set(
      (therapistRows ?? [])
        .map((t) => employeeToProfile.get(t.employee_id))
        .filter((id): id is string => !!id),
    );

    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('clinic_id', clinicId)
      .in('role', ['therapist', 'administrator', 'receptionist'])
      .eq('status', 'active');

    if (error) throw error;

    return (profiles ?? []).filter((p) => !usedProfileIds.has(p.id)) as Profile[];
  }

  async create(clinicId: string, form: TherapistFormData): Promise<TherapistWithProfile> {
    const supabase = getSupabaseClient();

    let employeeId: string;
    const { data: existingEmployee } = await supabase
      .from('employees')
      .select('id')
      .eq('clinic_id', clinicId)
      .eq('profile_id', form.profile_id)
      .maybeSingle();

    if (existingEmployee) {
      employeeId = existingEmployee.id;
    } else {
      const { data: newEmployee, error: empError } = await supabase
        .from('employees')
        .insert({
          clinic_id: clinicId,
          profile_id: form.profile_id,
          job_title: 'Terapeuta',
          status: 'active',
        })
        .select()
        .single();
      if (empError) throw empError;
      employeeId = newEmployee.id;
    }

    await supabase
      .from('profiles')
      .update({ role: 'therapist' })
      .eq('id', form.profile_id);

    const { data, error } = await supabase
      .from('therapists')
      .insert({
        clinic_id: clinicId,
        employee_id: employeeId,
        specialty: form.specialty || null,
        bio: form.bio || null,
        skills: form.skills ?? [],
        color: form.color || '#2F4F46',
        photo_url: form.photo_url || null,
        status: form.status ?? 'active',
      })
      .select()
      .single();

    if (error) throw error;
    const enriched = await this.enrichWithProfiles([data]);
    return enriched[0];
  }

  async update(id: string, form: Partial<TherapistFormData>): Promise<TherapistWithProfile> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('therapists')
      .update({
        ...(form.specialty !== undefined && { specialty: form.specialty || null }),
        ...(form.bio !== undefined && { bio: form.bio || null }),
        ...(form.skills !== undefined && { skills: form.skills }),
        ...(form.color !== undefined && { color: form.color || null }),
        ...(form.photo_url !== undefined && { photo_url: form.photo_url || null }),
        ...(form.status !== undefined && { status: form.status }),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    const enriched = await this.enrichWithProfiles([data]);
    return enriched[0];
  }

  async archive(id: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from('therapists').update({ status: 'archived' }).eq('id', id);
    if (error) throw error;
  }

  async uploadPhoto(clinicId: string, therapistId: string, file: File): Promise<string> {
    const supabase = getSupabaseClient();
    const ext = file.name.split('.').pop() ?? 'jpg';
    const path = `${clinicId}/therapists/${therapistId}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) throw uploadError;

    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    return data.publicUrl;
  }

  // --- Horários ---

  async getWorkingHours(therapistId: string): Promise<WorkingHour[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('working_hours')
      .select('*')
      .eq('therapist_id', therapistId)
      .order('day_of_week');
    if (error) throw error;
    return (data ?? []) as WorkingHour[];
  }

  async saveWorkingHours(
    clinicId: string,
    therapistId: string,
    hours: WorkingHourFormData[],
  ): Promise<void> {
    const supabase = getSupabaseClient();

    await supabase.from('working_hours').delete().eq('therapist_id', therapistId);

    if (hours.length === 0) return;

    const { error } = await supabase.from('working_hours').insert(
      hours.map((h) => ({
        clinic_id: clinicId,
        therapist_id: therapistId,
        day_of_week: h.day_of_week,
        start_time: h.start_time,
        end_time: h.end_time,
        is_active: h.is_active ?? true,
      })),
    );
    if (error) throw error;
  }

  // --- Bloqueios ---

  async getBlockedPeriods(therapistId: string): Promise<BlockedPeriod[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('blocked_periods')
      .select('*')
      .eq('therapist_id', therapistId)
      .order('start_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as BlockedPeriod[];
  }

  async addBlockedPeriod(
    clinicId: string,
    therapistId: string,
    form: BlockedPeriodFormData,
  ): Promise<BlockedPeriod> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('blocked_periods')
      .insert({
        clinic_id: clinicId,
        therapist_id: therapistId,
        start_at: form.start_at,
        end_at: form.end_at,
        reason: form.reason || null,
      })
      .select()
      .single();
    if (error) throw error;
    return data as BlockedPeriod;
  }

  async removeBlockedPeriod(id: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from('blocked_periods').delete().eq('id', id);
    if (error) throw error;
  }

  /**
   * Terapeutas em serviço agora (horário de hoje + sem bloqueio activo)
   */
  async getOnDutyTherapists(clinicId: string): Promise<{ id: string; full_name: string }[]> {
    const supabase = getSupabaseClient();
    const now = new Date();
    const dayOfWeek = now.getDay();
    const timeNow = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const nowIso = now.toISOString();

    const { data: therapists, error: therapistsError } = await supabase
      .from('therapists')
      .select('id, employee_id, status')
      .eq('clinic_id', clinicId)
      .eq('status', 'active');

    if (therapistsError) throw therapistsError;
    if (!therapists?.length) return [];

    const therapistIds = therapists.map((t) => t.id);

    const [{ data: hours, error: hoursError }, { data: blocked, error: blockedError }] =
      await Promise.all([
        supabase
          .from('working_hours')
          .select('therapist_id, start_time, end_time')
          .eq('clinic_id', clinicId)
          .eq('day_of_week', dayOfWeek)
          .eq('is_active', true)
          .in('therapist_id', therapistIds),
        supabase
          .from('blocked_periods')
          .select('therapist_id')
          .eq('clinic_id', clinicId)
          .in('therapist_id', therapistIds)
          .lte('start_at', nowIso)
          .gte('end_at', nowIso),
      ]);

    if (hoursError) throw hoursError;
    if (blockedError) throw blockedError;

    const blockedIds = new Set((blocked ?? []).map((b) => b.therapist_id));
    const normalizeTime = (value: string) => value.slice(0, 5);

    const onDutyIds = new Set(
      (hours ?? [])
        .filter((hour) => {
          const start = normalizeTime(hour.start_time);
          const end = normalizeTime(hour.end_time);
          return start <= timeNow && end > timeNow && !blockedIds.has(hour.therapist_id);
        })
        .map((hour) => hour.therapist_id),
    );

    const onDutyTherapists = therapists.filter((t) => onDutyIds.has(t.id));
    const enriched = await this.enrichWithProfiles(onDutyTherapists);

    return enriched.map((t) => ({ id: t.id, full_name: t.full_name }));
  }

  private async enrichWithProfiles(
    therapists: { id: string; employee_id: string; [key: string]: unknown }[],
  ): Promise<TherapistWithProfile[]> {
    if (!therapists.length) return [];

    const supabase = getSupabaseClient();
    const employeeIds = therapists.map((t) => t.employee_id);

    const { data: employees } = await supabase
      .from('employees')
      .select('id, profile_id')
      .in('id', employeeIds);

    if (!employees?.length) return [];

    const profileIds = employees.map((e) => e.profile_id);
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email, phone')
      .in('id', profileIds);

    const employeeToProfile = new Map(employees.map((e) => [e.id, e.profile_id]));
    const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));

    return therapists.map((t) => {
      const profileId = employeeToProfile.get(t.employee_id);
      const profile = profileId ? profileMap.get(profileId) : null;
      return {
        ...t,
        full_name: profile?.full_name ?? '—',
        email: profile?.email ?? '',
        phone: profile?.phone ?? null,
      } as TherapistWithProfile;
    });
  }
}

export const therapistService = new TherapistService();
